/**
 * Video Compression Utility
 * Compresses videos client-side to save storage while maintaining quality
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  videoBitrate?: number; // kbps
  audioBitrate?: number; // kbps
  maxSizeMB?: number;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1280,
  maxHeight: 720,
  videoBitrate: 2500, // 2.5 Mbps - good quality for 720p
  audioBitrate: 128,  // 128 kbps
  maxSizeMB: 50       // Max file size
};

/**
 * Compress a video file using HTML5 Canvas and MediaRecorder API
 */
export async function compressVideo(
  file: File,
  options: CompressionOptions = {},
  onProgress?: (progress: number) => void
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Check if file is already small enough
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB < 5) {
    // Skip compression for files under 5MB
    if (onProgress) onProgress(100);
    return file;
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    video.onloadedmetadata = async () => {
      try {
        URL.revokeObjectURL(video.src);

        // Calculate scaled dimensions maintaining aspect ratio
        let { videoWidth: width, videoHeight: height } = video;
        const aspectRatio = width / height;

        if (width > (opts.maxWidth || 1280)) {
          width = opts.maxWidth || 1280;
          height = Math.round(width / aspectRatio);
        }
        if (height > (opts.maxHeight || 720)) {
          height = opts.maxHeight || 720;
          width = Math.round(height * aspectRatio);
        }

        // Create canvas for video frames
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;

        // Setup MediaRecorder
        const stream = canvas.captureStream(30); // 30 fps
        
        // Add audio track if present
        try {
          const videoStream = (video as any).captureStream?.();
          if (videoStream) {
            const audioTracks = videoStream.getAudioTracks();
            if (audioTracks.length > 0) {
              stream.addTrack(audioTracks[0]);
            }
          }
        } catch (e) {
          // Audio capture not supported, continue without audio
        }

        const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
          ? 'video/webm;codecs=vp8'
          : 'video/webm';

        const recorder = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: (opts.videoBitrate || 2500) * 1000,
          audioBitsPerSecond: (opts.audioBitrate || 128) * 1000
        });

        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType });
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.webm'), {
            type: mimeType,
            lastModified: Date.now()
          });

          if (onProgress) onProgress(100);
          resolve(compressedFile);
        };

        recorder.onerror = (e) => {
          reject(new Error(`MediaRecorder error: ${e}`));
        };

        // Start recording
        recorder.start(100); // Collect data every 100ms

        // Play video and draw frames to canvas
        video.currentTime = 0;
        await video.play();

        const drawFrame = () => {
          if (video.paused || video.ended) {
            recorder.stop();
            video.remove();
            return;
          }

          ctx.drawImage(video, 0, 0, width, height);

          // Update progress
          if (onProgress && video.duration > 0) {
            const progress = Math.min(95, (video.currentTime / video.duration) * 100);
            onProgress(progress);
          }

          requestAnimationFrame(drawFrame);
        };

        drawFrame();

      } catch (error) {
        reject(error);
      }
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * Check if a file is a video
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith('video/');
}

/**
 * Get estimated compressed size (rough approximation)
 */
export function estimateCompressedSize(file: File, options: CompressionOptions = {}): number {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const fileSizeMB = file.size / (1024 * 1024);
  
  if (fileSizeMB < 5) return file.size;
  
  // Rough estimate: bitrate * duration
  // Assume average 1 minute video at specified bitrate
  const estimatedBitrate = ((opts.videoBitrate || 2500) + (opts.audioBitrate || 128)) * 1000;
  const estimatedSize = (estimatedBitrate * 60) / 8; // bits to bytes for 1 min
  
  return Math.min(file.size, estimatedSize);
}
